import { useState, useEffect } from "react";
import { Stack, TextField, Checkbox, PrimaryButton, Spinner, SpinnerSize, DefaultButton } from "@fluentui/react";
import { getOutputCSVAllData, downloadOutputCSVAll } from "../../../api/admin/api_admin";
import { OutputCSVAllResponse, UserOption, GroupOption } from "../../../api/admin/models_admin";
import { AdminLabel, ErrorDialog } from "../components";

export const OutputCSVAll = () => {
    const [data, setData] = useState<OutputCSVAllResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [downloading, setDownloading] = useState<boolean>(false);
    
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");
    const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
    const [selectedGroupCds, setSelectedGroupCds] = useState<Set<string>>(new Set());
    const [showUserCheckboxes, setShowUserCheckboxes] = useState<boolean>(false);
    const [showGroupCheckboxes, setShowGroupCheckboxes] = useState<boolean>(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await getOutputCSVAllData();
            if ("error" in result) {
                setError(result.error);
            } else {
                setData(result);
            }
        } catch (err) {
            setError("データの取得中にエラーが発生しました");
        } finally {
            setLoading(false);
        }
    };

    const handleUserCheckboxChange = (userid: string, checked: boolean) => {
        const newSet = new Set(selectedUserIds);
        if (checked) {
            newSet.add(userid);
        } else {
            newSet.delete(userid);
        }
        setSelectedUserIds(newSet);
    };

    const handleGroupCheckboxChange = (groupCd: string, checked: boolean) => {
        const newSet = new Set(selectedGroupCds);
        if (checked) {
            newSet.add(groupCd);
        } else {
            newSet.delete(groupCd);
        }
        setSelectedGroupCds(newSet);
    };

    const handleDownload = async () => {
        setDownloading(true);
        setError(null);
        try {
            const result = await downloadOutputCSVAll({
                startdate: startDate,
                enddate: endDate,
                userids: Array.from(selectedUserIds),
                group_cds: Array.from(selectedGroupCds)
            });
            
            if ("error" in result) {
                setError(result.error);
            } else {
                const url = window.URL.createObjectURL(result);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'conversation_log_all.csv';
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            }
        } catch (err) {
            setError("ダウンロード中にエラーが発生しました");
        } finally {
            setDownloading(false);
        }
    };

    if (loading) {
        return (
            <Stack horizontalAlign="center" verticalAlign="center" style={{ minHeight: '400px' }}>
                <Spinner size={SpinnerSize.large} label="読み込み中..." />
            </Stack>
        );
    }

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <AdminLabel text="ログ一括ダウンロード" />

            {
                <ErrorDialog 
                    content={error ?? ""} 
                    onDismiss={() => setError(null)} 
                    isOpen={Boolean(error)} />
            }

            <div style={{ fontSize: '0.9em', color: '#555' }}>
                ※日時を指定しない場合、全期間のログがダウンロードされます。
            </div>

            <Stack tokens={{ childrenGap: 15 }} styles={{ root: { maxWidth: 600 } }}>
                <TextField
                    label="開始日"
                    type="datetime-local"
                    value={startDate}
                    onChange={(_, newValue) => setStartDate(newValue || "")}
                    autoComplete="off"
                />

                <TextField
                    label="終了日"
                    type="datetime-local"
                    value={endDate}
                    onChange={(_, newValue) => setEndDate(newValue || "")}
                    autoComplete="off"
                />

                <Stack tokens={{ childrenGap: 10 }}>
                    <DefaultButton
                        text="ユーザーを選択(未選択で全ユーザー)"
                        onClick={() => setShowUserCheckboxes(!showUserCheckboxes)}
                        styles={{ root: { width: 300 } }}
                    />
                    {showUserCheckboxes && (
                        <Stack
                            styles={{
                                root: {
                                    border: '1px solid #dadada',
                                    maxHeight: 200,
                                    overflowY: 'auto',
                                    width: 300
                                }
                            }}
                        >
                            {data?.users.map((user: UserOption) => (
                                <div
                                    key={user.userid}
                                    style={{
                                        padding: '5px',
                                        cursor: 'pointer'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1e90ff'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                    <Checkbox
                                        label={user.USR_username}
                                        checked={selectedUserIds.has(user.userid)}
                                        onChange={(_, checked) => handleUserCheckboxChange(user.userid, !!checked)}
                                    />
                                </div>
                            ))}
                        </Stack>
                    )}
                </Stack>

                <Stack tokens={{ childrenGap: 10 }}>
                    <DefaultButton
                        text="グループを選択(未選択で全グループ)"
                        onClick={() => setShowGroupCheckboxes(!showGroupCheckboxes)}
                        styles={{ root: { width: 300 } }}
                    />
                    {showGroupCheckboxes && (
                        <Stack
                            styles={{
                                root: {
                                    border: '1px solid #dadada',
                                    maxHeight: 200,
                                    overflowY: 'auto',
                                    width: 300
                                }
                            }}
                        >
                            {data?.group_list.map((group: GroupOption) => (
                                <div
                                    key={group.GRP_group_cd}
                                    style={{
                                        padding: '5px',
                                        cursor: 'pointer'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1e90ff'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                    <Checkbox
                                        label={group.GRP_group_name}
                                        checked={selectedGroupCds.has(group.GRP_group_cd)}
                                        onChange={(_, checked) => handleGroupCheckboxChange(group.GRP_group_cd, !!checked)}
                                    />
                                </div>
                            ))}
                        </Stack>
                    )}
                </Stack>

                <PrimaryButton
                    text="ダウンロード"
                    onClick={handleDownload}
                    disabled={downloading}
                    styles={{ root: { width: 200 } }}
                />
            </Stack>
        </Stack>
    );
}